import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import type {QueryClient, QueryKey} from '@github-ui/react-query'
import type {SavedView} from '@github-ui/saved-views'

import type {ViewConfiguration} from '../contexts/views/view-configuration'

/** The request pathname `queryKey` was built with, if its deps have one. */
export function requestPathname(queryKey: QueryKey): string | undefined {
  const deps = queryKey[4] as {pathname?: unknown} | undefined
  return typeof deps?.pathname === 'string' ? deps.pathname : undefined
}

/** A route query key for `pathname` and `querySearchParams`, keeping the rest of `queryKey`. */
export function canonicalQueryKey(
  queryKey: RouteQueryKey,
  pathname: string,
  querySearchParams: Record<string, string>,
): RouteQueryKey {
  return [queryKey[0], queryKey[1], queryKey[2], queryKey[3], {pathname, searchParams: querySearchParams}]
}

/**
 * Copies a saved view route response to another key, with the saved configuration when given,
 * so a navigation after Save draws what is already on screen. `savedView` replaces the view only
 * in a response that has one, whether the server metadata is still top-level or has been normalized
 * under `view`. A board keeps the time it was first keyed by, so its loaded pages survive.
 */
export function copyRouteQueryData(
  client: QueryClient,
  from: RouteQueryKey,
  to: RouteQueryKey,
  configuration?: ViewConfiguration,
  savedView?: SavedView,
) {
  const queryData = client.getQueryData<{payload: Record<string, unknown>}>(from)
  if (queryData !== undefined) {
    const nestedView =
      typeof queryData.payload.view === 'object' && queryData.payload.view !== null
        ? (queryData.payload.view as Record<string, unknown>)
        : undefined
    const replacedView =
      savedView && ('savedView' in queryData.payload || (nestedView && 'savedView' in nestedView))
        ? savedView
        : undefined
    // Refresh route freshness for navigation, but retain the timestamp used to key the
    // loaded board. A Save acknowledges settings; it is not a new results response.
    const boardDataUpdatedAt =
      queryData.payload.layout === 'board'
        ? (queryData.payload.boardDataUpdatedAt ?? client.getQueryState(from)?.dataUpdatedAt)
        : undefined
    client.setQueryData(
      to,
      configuration || replacedView || boardDataUpdatedAt !== undefined
        ? {
            ...queryData,
            payload: {
              ...queryData.payload,
              ...(configuration ? {configuration} : {}),
              ...(replacedView
                ? nestedView
                  ? {view: {...nestedView, savedView: replacedView}}
                  : {savedView: replacedView}
                : {}),
              ...(boardDataUpdatedAt !== undefined ? {boardDataUpdatedAt} : {}),
            },
          }
        : queryData,
    )
  }
}
